import { Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";
import type { ScreenStatusEvent } from "@shared/types/foyer";

interface Props {
  events: ScreenStatusEvent[];
  days?: number;
  height?: number;
}

/**
 * Simple SVG online/offline timeline. Buckets the last N days into 24 slots
 * (one per hour for a day; one per day if days > 1) and shades each slot
 * green if the screen reported online during that window, grey otherwise.
 *
 * Pure SVG so it ships without a chart dep.
 */
export default function UptimeChart({ events, days = 14, height = 64 }: Props) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const bins = useMemo(() => computeBins(events, days), [events, days]);

  if (bins.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: "background.default",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("uptimeChart.empty")}
        </Typography>
      </Box>
    );
  }

  const totalOnlineRatio = bins.filter((b) => b.online).length / bins.length;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t("uptimeChart.summary", {
            count: days,
            pct: fmt.number(totalOnlineRatio, {
              style: "percent",
              maximumFractionDigits: 0,
            }),
          })}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Legend color="#4caf50" label={t("uptimeChart.legend.online")} />
          <Legend color="#ccc" label={t("uptimeChart.legend.offline")} />
          <Legend color="#eee" label={t("uptimeChart.legend.noData")} />
        </Stack>
      </Stack>
      <Box
        component="svg"
        viewBox={`0 0 ${bins.length} 1`}
        preserveAspectRatio="none"
        sx={{
          width: "100%",
          height,
          borderRadius: 1,
          bgcolor: "background.default",
          display: "block",
        }}
      >
        {bins.map((b, i) => (
          <rect
            key={i}
            x={i}
            y={0}
            width={1}
            height={1}
            fill={b.unknown ? "#eee" : b.online ? "#4caf50" : "#cccccc"}
          />
        ))}
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {bins[0]
            ? fmt.date(bins[0].startMs, { month: "short", day: "numeric" })
            : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("uptimeChart.now")}
        </Typography>
      </Stack>
    </Stack>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 0.5 }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

interface Bin {
  startMs: number;
  endMs: number;
  online: boolean;
  unknown: boolean;
}

function computeBins(events: ScreenStatusEvent[], days: number): Bin[] {
  const now = Date.now();
  const startMs = now - days * 86_400_000;
  // 48 buckets total - finer when days <= 1, coarser otherwise.
  const bucketCount = 48;
  const span = now - startMs;
  const step = span / bucketCount;

  // Sort events ascending.
  const sorted = [...events].sort(
    (a, b) => Date.parse(a.at) - Date.parse(b.at)
  );

  const bins: Bin[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const bs = startMs + i * step;
    const be = bs + step;
    // What was the screen's status at the START of this bucket?
    const before = sorted.filter((e) => Date.parse(e.at) <= bs);
    const lastBefore = before[before.length - 1];
    // Any transition inside the bucket?
    const inside = sorted.filter(
      (e) => Date.parse(e.at) > bs && Date.parse(e.at) <= be
    );
    let online: boolean;
    let unknown = false;
    if (lastBefore) {
      online = lastBefore.online;
    } else if (inside.length > 0) {
      // No state before, but a transition during the bucket - treat as
      // unknown until first event.
      online = inside[0].online;
      unknown = false;
    } else {
      online = false;
      unknown = true;
    }
    // If transitioned online during the bucket, count the bucket online.
    if (inside.some((e) => e.online)) online = true;
    bins.push({ startMs: bs, endMs: be, online, unknown });
  }
  return bins;
}
